import React from 'react';
import { Box, Divider, Flex, SimpleGrid, Tag, Text } from '@chakra-ui/react';
import { FaCalendarAlt, FaClock } from 'react-icons/fa';
import { formatVariantSchedule, getVariantPrice } from '../../utils';
import { countVariantOccupiedSeats } from '../../utils/guestEventEligibility';

/**
 * Layout effettivo: quello scelto in admin (evento.layoutVarianti),
 * oppure in automatico griglia compatta per tante varianti senza descrizione.
 */
export const resolveVariantLayout = (layout, varianti = []) => {
  if (layout && layout !== 'auto') return layout;
  const hasDescriptions = varianti.some((v) => v.Descrizione?.trim());
  return !hasDescriptions && varianti.length >= 6 ? 'griglia' : 'schede';
};

export const getSeats = (variant) => {
  const available = variant.Capienza
    ? variant.Capienza - countVariantOccupiedSeats(variant)
    : 0;
  const ratio = variant.Capienza ? available / variant.Capienza : 0;
  return {
    available,
    ratio,
    // Senza capienza la variante non si esaurisce
    soldOut: Boolean(variant.Capienza) && available <= 0,
    color: ratio < 0.25 ? 'red.400' : ratio < 0.5 ? 'yellow.400' : 'green.500',
  };
};

const PriceLabel = ({ price, ...props }) => (
  <Text
    fontWeight="bold"
    color={price === 0 ? 'green.600' : 'gray.800'}
    {...props}
  >
    {price === 0 ? 'Gratis' : `€ ${price}`}
  </Text>
);

const Schedule = ({ variant, compact = false }) => {
  const { date, time } = formatVariantSchedule(variant);
  if (!date) return null;
  if (compact) {
    return (
      <Text fontSize="xs" color="gray.600" lineHeight="short">
        {date}
        {time ? (
          <Box as="span" display="block">
            {time}
          </Box>
        ) : null}
      </Text>
    );
  }
  return (
    <Flex wrap="wrap" columnGap={4} rowGap={1} fontSize="sm" color="gray.600">
      <Flex align="center" gap={2}>
        <FaCalendarAlt />
        <Text>{date}</Text>
      </Flex>
      {time ? (
        <Flex align="center" gap={2}>
          <FaClock />
          <Text>{time}</Text>
        </Flex>
      ) : null}
    </Flex>
  );
};

const SeatsBar = ({ variant, seats }) => (
  <Box>
    <Flex justify="space-between" fontSize="sm" mb={1}>
      <Text fontWeight="semibold">Posti</Text>
      <Text color="gray.600">
        {seats.soldOut
          ? 'Posti esauriti'
          : `${seats.available} su ${variant.Capienza}`}
      </Text>
    </Flex>
    <Box h="8px" bg="gray.100" borderRadius="full" overflow="hidden">
      <Box
        h="100%"
        w={`${Math.round((1 - seats.ratio) * 100)}%`}
        bg={seats.soldOut ? 'red.400' : seats.color}
        borderRadius="full"
      />
    </Box>
  </Box>
);

/** Props comuni per rendere cliccabile una variante (apre il drawer su quell'attività). */
const clickableProps = (clickable, onSelect, variant) => ({
  cursor: clickable ? 'pointer' : 'default',
  onClick: () => {
    if (clickable) onSelect(variant);
  },
  _hover: clickable ? { borderColor: 'blue.400', boxShadow: 'sm' } : {},
  transition: 'box-shadow 0.15s, border-color 0.15s',
});

const SoloSociTag = ({ show, ...props }) =>
  show ? (
    <Tag size="sm" colorScheme="purple" variant="subtle" {...props}>
      Solo soci
    </Tag>
  ) : null;

const SchedaVariante = ({
  variant,
  event,
  seats,
  clickable,
  onSelect,
  soloSoci,
}) => (
  <Flex
    direction="column"
    gap={2}
    p={4}
    border="1px solid"
    borderColor="gray.200"
    borderLeft="4px solid"
    borderLeftColor={seats.soldOut ? 'red.400' : seats.color}
    borderRadius="md"
    opacity={seats.soldOut ? 0.7 : 1}
    {...clickableProps(clickable, onSelect, variant)}
  >
    <Flex justify="space-between" align="baseline" gap={3}>
      <Text fontWeight="bold" fontSize="md" color="gray.800">
        {variant.Nome || 'Variante senza nome'}
      </Text>
      <PriceLabel
        price={getVariantPrice(variant, event)}
        fontSize="lg"
        flexShrink={0}
      />
    </Flex>
    <SoloSociTag show={soloSoci} alignSelf="flex-start" />
    <Schedule variant={variant} />
    {variant.Descrizione ? (
      <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
        {variant.Descrizione}
      </Text>
    ) : null}
    {variant.Capienza ? (
      <Box mt="auto" pt={1}>
        <SeatsBar variant={variant} seats={seats} />
      </Box>
    ) : null}
  </Flex>
);

const CellaVariante = ({
  variant,
  event,
  seats,
  clickable,
  onSelect,
  soloSoci,
}) => (
  <Flex
    direction="column"
    gap={1}
    p={3}
    border="1px solid"
    borderColor="gray.200"
    borderTop="3px solid"
    borderTopColor={seats.soldOut ? 'red.400' : seats.color}
    borderRadius="md"
    textAlign="center"
    opacity={seats.soldOut ? 0.6 : 1}
    {...clickableProps(clickable, onSelect, variant)}
  >
    <Text fontWeight="bold" fontSize="sm" color="gray.800" noOfLines={2}>
      {variant.Nome || 'Variante'}
    </Text>
    <Schedule variant={variant} compact />
    <SoloSociTag show={soloSoci} alignSelf="center" />
    <Box mt="auto" pt={1}>
      {seats.soldOut ? (
        <Text fontSize="xs" fontWeight="bold" color="red.500">
          Esaurita
        </Text>
      ) : (
        <>
          <PriceLabel price={getVariantPrice(variant, event)} fontSize="sm" />
          {variant.Capienza ? (
            <Text fontSize="xs" color="gray.500">
              {seats.available} posti
            </Text>
          ) : null}
        </>
      )}
    </Box>
  </Flex>
);

const RigaVariante = ({
  variant,
  event,
  seats,
  clickable,
  onSelect,
  soloSoci,
}) => (
  <Flex
    direction={{ base: 'column', sm: 'row' }}
    align={{ base: 'stretch', sm: 'center' }}
    gap={{ base: 1, sm: 4 }}
    py={3}
    px={2}
    borderRadius="md"
    opacity={seats.soldOut ? 0.6 : 1}
    {...clickableProps(clickable, onSelect, variant)}
    _hover={clickable ? { bg: 'gray.50' } : {}}
  >
    <Box flex={1} minW={0}>
      <Text fontWeight="bold" color="gray.800">
        {variant.Nome || 'Variante senza nome'}
      </Text>
      <SoloSociTag show={soloSoci} />
      <Schedule variant={variant} />
      {variant.Descrizione ? (
        <Text fontSize="sm" color="gray.600" mt={1} whiteSpace="pre-wrap">
          {variant.Descrizione}
        </Text>
      ) : null}
    </Box>
    <Flex gap={4} align="center" justify="space-between" flexShrink={0}>
      {variant.Capienza ? (
        <Text fontSize="sm" color={seats.soldOut ? 'red.500' : 'gray.600'}>
          {seats.soldOut ? 'Esaurita' : `${seats.available} posti`}
        </Text>
      ) : null}
      <PriceLabel
        price={getVariantPrice(variant, event)}
        minW="70px"
        textAlign="right"
      />
    </Flex>
  </Flex>
);

/**
 * Varianti dentro la pagina evento: schede (poche, con descrizione),
 * griglia compatta (tante e uguali, es. settimane) o lista.
 * isSelectable: per gli ospiti, solo le varianti aperte a tutti; le altre mostrano "Solo soci".
 */
const EmbeddedVariants = ({
  varianti = [],
  event,
  canClick,
  onSelect,
  isSelectable,
}) => {
  const layout = resolveVariantLayout(event?.layoutVarianti, varianti);
  const items = varianti.map((variant) => {
    const seats = getSeats(variant);
    const selectable = isSelectable ? isSelectable(variant) : true;
    return {
      variant,
      seats,
      clickable: canClick && !seats.soldOut && selectable,
      soloSoci: !selectable,
    };
  });

  if (layout === 'lista') {
    return (
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" px={2}>
        {items.map((item, i) => (
          <React.Fragment key={item.variant.documentId || item.variant.id}>
            {i > 0 && <Divider />}
            <RigaVariante event={event} onSelect={onSelect} {...item} />
          </React.Fragment>
        ))}
      </Box>
    );
  }

  if (layout === 'griglia') {
    return (
      <SimpleGrid minChildWidth="140px" spacing={3}>
        {items.map((item) => (
          <CellaVariante
            key={item.variant.documentId || item.variant.id}
            event={event}
            onSelect={onSelect}
            {...item}
          />
        ))}
      </SimpleGrid>
    );
  }

  return (
    <SimpleGrid
      columns={{
        base: 1,
        md: items.length === 1 ? 1 : 2,
        xl: items.length % 3 === 0 ? 3 : items.length === 1 ? 1 : 2,
      }}
      spacing={4}
    >
      {items.map((item) => (
        <SchedaVariante
          key={item.variant.documentId || item.variant.id}
          event={event}
          onSelect={onSelect}
          {...item}
        />
      ))}
    </SimpleGrid>
  );
};

export default EmbeddedVariants;
